import {
  limparFavoritosUsuario,
  salvarFavoritoUsuario,
  sincronizarFavoritosUsuario,
  type FavoritosPorDominio,
} from "../api/favoritosUsuario";
import { mostrarToastErro } from "./toast";

const PREFIXO = "favoritos";
const cacheFavoritosPorLogin = new Map<string, Map<string, Set<string>>>();
const metadadosFavorito = Symbol("favoritoUsuario");
type ReferenciaFavorito = { dominio: string; referenciaId: string };
const ouvintesFavoritos = new Set<(login: string) => void>();
const filasFavoritos = new Map<string, Promise<void>>();
const pendenciasFavoritos = new Map<string, number>();
let loginAtivo = "";

export function referenciaFavoritoDoRegistro(registro: unknown): ReferenciaFavorito | undefined {
  return registro && typeof registro === "object"
    ? (registro as { [metadadosFavorito]?: ReferenciaFavorito })[metadadosFavorito]
    : undefined;
}

export function observarFavoritosDoLogin(usuarioId: string | number | undefined, atualizar: () => void): () => void {
  const ouvinte = (login: string) => { if (login === String(usuarioId ?? "")) atualizar(); };
  ouvintesFavoritos.add(ouvinte);
  return () => { ouvintesFavoritos.delete(ouvinte); };
}

function notificarFavoritos(login: string): void {
  ouvintesFavoritos.forEach((ouvinte) => ouvinte(login));
}

export function favoritosEmAndamento(usuarioId: string | number | undefined, dominio: string): boolean {
  return Boolean(pendenciasFavoritos.get(`${usuarioId ?? ""}:${dominio}`));
}

/** Ordena gravações e limpeza; a memória só muda após confirmação do banco. */
function persistirFavoritos(usuarioId: string | number | undefined, dominio: string, salvar: () => Promise<unknown>, confirmar: () => void): void {
  const login = String(usuarioId ?? "");
  if (!login || login !== loginAtivo) return;
  const chaveFila = `${login}:${dominio}`;
  pendenciasFavoritos.set(chaveFila, (pendenciasFavoritos.get(chaveFila) || 0) + 1);
  notificarFavoritos(login);
  const operacao: Promise<void> = (filasFavoritos.get(chaveFila) || Promise.resolve())
    .then(async () => {
      // Uma tarefa de um login anterior não pode gravar com a sessão de outro usuário.
      if (login !== loginAtivo) return;
      await salvar();
      confirmar();
    })
    .catch((erro) => { mostrarToastErro(erro, "Não foi possível salvar os favoritos."); })
    .finally(() => {
      const pendentes = (pendenciasFavoritos.get(chaveFila) || 1) - 1;
      if (pendentes) pendenciasFavoritos.set(chaveFila, pendentes);
      else pendenciasFavoritos.delete(chaveFila);
      if (filasFavoritos.get(chaveFila) === operacao) filasFavoritos.delete(chaveFila);
      notificarFavoritos(login);
    });
  filasFavoritos.set(chaveFila, operacao);
}

function chave(usuarioId: string | number | undefined, dominio: string): string {
  return `${PREFIXO}:${usuarioId ?? "anon"}:${dominio}`;
}

function chaveMigracaoServidor(usuarioId: string | number | undefined): string {
  return `${PREFIXO}:servidor-migrado:${usuarioId ?? "anon"}`;
}

function lerConjunto(usuarioId: string | number | undefined, dominio: string): Set<string> {
  return new Set(cacheFavoritosPorLogin.get(String(usuarioId ?? "anon"))?.get(dominio) ?? []);
}

function gravarConjunto(usuarioId: string | number | undefined, dominio: string, conjunto: Set<string>): void {
  const login = String(usuarioId ?? "anon");
  const favoritos = cacheFavoritosPorLogin.get(login) ?? new Map<string, Set<string>>();
  favoritos.set(dominio, new Set(conjunto));
  cacheFavoritosPorLogin.set(login, favoritos);
  notificarFavoritos(login);
}

/**
 * O cache em memória acompanha o favorito individual confirmado no banco
 * para o login autenticado.
 */
export function sincronizarFavoritosLocais<T>(
  usuarioId: string | number | undefined,
  dominio: string,
  _itens: readonly T[],
  _idDoItem: (item: T) => string | number,
  _eraFavoritoNoServidor: (item: T) => boolean
): Set<string> {
  return lerConjunto(usuarioId, dominio);
}

function favoritosLocaisDoUsuario(usuarioId: string | number): FavoritosPorDominio {
  const prefixo = `${PREFIXO}:${usuarioId}:`;
  const favoritos: FavoritosPorDominio = {};
  try {
    for (let indice = 0; indice < localStorage.length; indice += 1) {
      const chaveLocal = localStorage.key(indice);
      if (!chaveLocal?.startsWith(prefixo)) continue;
      const dominio = chaveLocal.slice(prefixo.length);
      if (!dominio) continue;
      const bruto = localStorage.getItem(chaveLocal);
      const referencias = bruto ? JSON.parse(bruto) : [];
      favoritos[dominio] = Array.isArray(referencias) ? referencias.map(String) : [];
    }
  } catch {
    // Sem cache local, a sincronizacao continua usando o banco.
  }
  return favoritos;
}

/**
 * Hidrata a memória pelo banco antes das listagens. O navegador é lido apenas
 * uma vez para migrar favoritos legados; não é usado como persistência nova.
 */
export async function sincronizarFavoritosDoLogin(usuarioId: string | number | undefined): Promise<void> {
  loginAtivo = String(usuarioId ?? "");
  if (usuarioId === undefined || usuarioId === null || String(usuarioId) === "") return;
  // Uma recarga da sessão não pode sobrescrever uma gravação ainda em andamento.
  await Promise.all([...filasFavoritos.entries()]
    .filter(([fila]) => fila.startsWith(`${usuarioId}:`))
    .map(([, operacao]) => operacao));
  if (loginAtivo !== String(usuarioId)) return;
  const jaMigrado = (() => {
    try {
      return localStorage.getItem(chaveMigracaoServidor(usuarioId)) === "1";
    } catch {
      return true;
    }
  })();
  const locais = favoritosLocaisDoUsuario(usuarioId);
  const resposta = await sincronizarFavoritosUsuario(jaMigrado ? {} : locais);
  if (loginAtivo !== String(usuarioId)) return;
  const favoritosServidor = resposta.favoritos ?? {};
  cacheFavoritosPorLogin.set(String(usuarioId), new Map());
  const dominios = new Set([...Object.keys(locais), ...Object.keys(favoritosServidor)]);
  dominios.forEach((dominio) => gravarConjunto(usuarioId, dominio, new Set(favoritosServidor[dominio] ?? [])));
  try {
    localStorage.setItem(chaveMigracaoServidor(usuarioId), "1");
    Object.keys(locais).forEach((dominio) => localStorage.removeItem(chave(usuarioId, dominio)));
  } catch {
    // O banco continua sendo a fonte de verdade sem suporte a localStorage.
  }
}

export function isFavoritoLocal(usuarioId: string | number | undefined, dominio: string, id: string | number): boolean {
  return lerConjunto(usuarioId, dominio).has(String(id));
}

/** Marca/desmarca no banco do login; somente o resultado confirmado atualiza a estrela. */
export function alternarFavoritoLocal(
  usuarioId: string | number | undefined,
  dominio: string,
  id: string | number,
  favorito: boolean
): void {
  persistirFavoritos(usuarioId, dominio, () => salvarFavoritoUsuario(dominio, id, favorito), () => {
    const conjunto = lerConjunto(usuarioId, dominio);
    if (favorito) conjunto.add(String(id));
    else conjunto.delete(String(id));
    gravarConjunto(usuarioId, dominio, conjunto);
  });
}

/** Remove os favoritos deste domínio para o login atual. */
export function limparFavoritosLocais(usuarioId: string | number | undefined, dominio: string): void {
  persistirFavoritos(usuarioId, dominio, () => limparFavoritosUsuario(dominio), () => gravarConjunto(usuarioId, dominio, new Set()));
}

/**
 * Aplica o estado do login, mantido no cache hidratado pelo banco, sobre a lista da API.
 */
export function aplicarFavoritosLocais<T>(
  usuarioId: string | number | undefined,
  dominio: string,
  itens: readonly T[],
  idDoItem: (item: T) => string | number
): T[] {
  const favoritos = sincronizarFavoritosLocais(usuarioId, dominio, itens, idDoItem, () => false);
  return itens.map((item) => {
    const favorito = favoritos.has(String(idDoItem(item)));
    return {
      ...item,
      // O Symbol acompanha clones da linha, mas não é enviado em JSON às APIs.
      [metadadosFavorito]: { dominio, referenciaId: String(idDoItem(item)) },
      /* Algumas APIs usam boolean e outras o texto legado "true"/"false". */
      favorito: typeof (item as { favorito?: unknown }).favorito === "boolean" ? favorito : favorito ? "true" : "false",
    } as T;
  });
}
